import React, { useEffect, useState } from "react";
import { fetchData } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../common/GeneralComponent";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import DataTable from "react-data-table-component";
import { Minus, Plus, Download } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../../components/partials/Loader";
import * as XLSX from "xlsx";
import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";

const Union = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const [ucs, setUcs] = useState([]);
  const [search, setSearch] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUcForDelete, setSelectedUcForDelete] = useState(null);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [tempDistrict, setTempDistrict] = useState(null);
  const [tempTehsil, setTempTehsil] = useState(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const [loader, setLoader] = useState(true);
  const fetchUcs = async () => {
    try {
      const response = await fetchData(
        "all-ucs",
        "GET",
        null,
        {},
        {
          search: search || undefined,
          page: pagination.currentPage,
          limit: pagination.pageSize,
          district_id: selectedDistrict || undefined,
          tehsil_id: selectedTehsil || undefined,
        },
        navigate,
        "/ucs",
        dispatch
      );
      setUcs(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      console.error("Error fetching UCs:", error);
    } finally {
      setLoader(false);
    }
  };
  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      const queryParams = {
        search: search || undefined,
        district_id: selectedDistrict || undefined,
        tehsil_id: selectedTehsil || undefined,
        page: 1,
        limit: 10000,
      };
      const response = await fetchData(
        "all-ucs",
        "GET",
        null,
        {},
        queryParams,
        navigate,
        "/ucs",
        dispatch
      );

      if (!response?.data || response?.data.length === 0) {
        toast.warning("No union councils found to export");
        return;
      }

      const allUcs = response.data;
      const excelData = allUcs.map((uc, index) => ({
        "Sr. No": index + 1,
        "UC Name": uc.UC_Name || uc.uc_name || "",
        "Tehsil Name":
          uc?.Code_Tehsil?.Tehsil_Name || uc?.tehsil?.tehsil_name || "N/A",
        "District Name":
          uc?.Code_District?.District_Name ||
          uc?.district?.district_name ||
          "N/A",
        Status: (uc.Status ?? uc.status) === 1 ? "Active" : "Inactive",
        "Created At": uc.created_at
          ? new Date(uc.created_at).toLocaleDateString()
          : "",
        "Updated At": uc.updated_at
          ? new Date(uc.updated_at).toLocaleDateString()
          : "",
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Union Councils");
      XLSX.writeFile(
        workbook,
        `union_councils_export_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      toast.success(
        `Successfully exported ${allUcs.length} union councils to Excel`
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export union councils to Excel");
    } finally {
      setIsExporting(false);
    }
  };
  const fetchDistricts = async () => {
    try {
      const res = await fetchData(
        "districts",
        "GET",
        null,
        {},
        {},
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(res?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };
  const fetchTehsils = async () => {
    if (!tempDistrict) return;
    try {
      const res = await fetchData(
        "tehsils",
        "GET",
        null,
        {},
        { district_id: tempDistrict },
        navigate,
        "/tehsils",
        dispatch
      );
      setTehsils(res?.data || []);
    } catch (error) {
      console.error("Error fetching tehsils:", error);
    }
  };
  useEffect(() => {
    fetchDistricts();
  }, []);

  useEffect(() => {
    fetchUcs();
  }, [
    pagination.currentPage,
    pagination.pageSize,
    search,
    selectedDistrict,
    selectedTehsil,
  ]);

  useEffect(() => {
    if (tempDistrict) fetchTehsils();
    else {
      setTehsils([]);
      setTempTehsil(null);
    }
  }, [tempDistrict]);
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedTehsil(null);
    setSelectedDistrict(null);
    setTempDistrict(null);
    setTempTehsil(null);
    setDistricts([]);
    setTehsils([]);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchDistricts();
  };
  const handleDeleteConfirm = async () => {
    if (!selectedUcForDelete) return;
    setIsDeleting(true);
    try {
      await fetchData(
        "ucs",
        "DELETE",
        { uc_id: selectedUcForDelete.id },
        {},
        {},
        navigate,
        "/ucs",
        dispatch
      );
      toast.success("Union deleted successfully.");
      setModalVisible(false);
      setSelectedUcForDelete(null);
      await fetchUcs();
    } catch (error) {
      console.error("Error deleting UC:", error);
      toast.error(error?.message || "Failed to delete Union.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setModalVisible(false);
    setSelectedUcForDelete(null);
  };

  const showDeleteModal = (row) => {
    const ucId = row.UC_Id || row.uc_id;
    const ucName = row.UC_Name || row.uc_name;
    setSelectedUcForDelete({ id: ucId, name: ucName });
    setModalVisible(true);
  };

  const toggleFilters = () => setIsFiltersVisible((prev) => !prev);
  const columns = [
    {
      name: "Sr.No",
      cell: (row, index) =>
        (pagination.currentPage - 1) * pagination.pageSize + index + 1,
      width: "80px",
    },
    {
      name: "UC Name",
      selector: (row) => row.UC_Name || row.uc_name,
      sortable: true,
    },
    {
      name: "Tehsil Name",
      selector: (row) =>
        row?.Code_Tehsil?.Tehsil_Name || row?.tehsil?.tehsil_name || "N/A",
      sortable: true,
    },
    {
      name: "District Name",
      selector: (row) =>
        row?.Code_District?.District_Name ||
        row?.district?.district_name ||
        "N/A",
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => {
        const status = row.Status ?? row.status;
        if (status === null || status === undefined) return "-";
        return (
          <span
            className={`py-1 px-2 text-[0.75em] rounded ${
              status === 1 ? "bg-success text-white" : "bg-red-500 text-white"
            }`}
          >
            {status === 1 ? "Active" : "In-Active"}
          </span>
        );
      },
    },
  ];

  {
    if (
      permissions.includes("ucs-update") ||
      permissions.includes("ucs-delete")
    ) {
      columns.push({
        name: <span className="text-[0.813rem]">Action</span>,
        cell: (row) => (
          <div className="flex gap-2">
            {/* <Link to={`/ucs/edit/${row.UC_Id || row.uc_id}`}>
          <i className="fa-solid fa-pen-to-square fa-lg"></i>
        </Link>
        <button
          onClick={() => showDeleteModal(row)}
          disabled={isDeleting}
          className={`${isDeleting ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <i className="fa fa-trash fa-lg text-primaryDark"></i>
        </button> */}
            <PermissionButton
              hasPermission={permissions.includes("ucs-update")}
              to={`/ucs/edit/${row.UC_Id || row.uc_id}`}
              faIcon="fa-solid fa-pen-to-square fa-lg"
              onlyIcon={true}
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed"
            />
            <PermissionButton
              hasPermission={permissions.includes("ucs-delete")}
              faIcon="fa fa-trash fa-lg text-primaryDark"
              onlyIcon
              onClick={() => showDeleteModal(row)}
              className="p-1 rounded-full bg-gray-200 text-[#137e81] hover:text-white"
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
            />
          </div>
        ),
      });
    }
  }

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "",
        color: "black",
        fontWeight: "bold",
        fontSize: "1rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  return (
    <div>
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Union Council
          </h1>
          <div className="flex gap-3">
            {ucs.length > 0 && (
              <button
                onClick={exportToExcel}
                disabled={isExporting}
                className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
                  isExporting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Download size={16} />
                {isExporting ? "Exporting..." : "Export Excel"}
              </button>
            )}
            <PermissionButton
              hasPermission={permissions.includes("ucs-create")}
              to="add"
              faIcon="fa fa-plus"
              text="Add"
              className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
              enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
              disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
            />
          </div>
        </div>
        <Accordion>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? <Minus color="#fff" /> : <Plus color="#fff" />
            }
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important",
              "& .MuiAccordionSummary-content": { color: "#fff !important" },
            }}
            onClick={toggleFilters}
          >
            Filters
          </AccordionSummary>
          <AccordionDetails>
            <div className="flex gap-2">
              <ThinDropdownWithSearch required={false}
                label="District"
                options={districts?.map((d) => ({
                  value: d.District_Id || d.district_id,
                  label: d.District_Name || d.district_name,
                }))}
                value={tempDistrict}
                onChange={(val) => setTempDistrict(val)}
              />
              <ThinDropdownWithSearch required={false}
                label="Tehsil"
                options={tehsils?.map((t) => ({
                  value: t.Tehsil_Id || t.tehsil_id,
                  label: t.Tehsil_Name || t.tehsil_name,
                }))}
                value={tempTehsil}
                onChange={(val) => setTempTehsil(val)}
              />
              <div className="flex gap-2 items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={() => {
                    setSelectedDistrict(tempDistrict);
                    setSelectedTehsil(tempTehsil);
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                  }}
                  className="px-4 py-2 bg-[#137e81] text-white rounded hover:bg-[#0f6b6e] transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
        <div className="bg-white rounded-lg shadow w-full mt-4 mb-[7rem]">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 justify-between border-lightGray border-b">
            <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
              Union Council List
            </h1>
          </div>
          <div className="p-[1.25rem] w-full">
            <div className="flex lg:flex-row flex-col justify-between gap-2 mb-4 w-full">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                ]}
              />
              <input
                onChange={handleSearchChange}
                placeholder="Search..."
                type="search"
                className="bg-[#ebeef2] border-[#ebeef2] rounded-md px-3 py-1 outline-none"
              />
            </div>
            <DataTable
              columns={columns}
              data={ucs}
              customStyles={customStyles}
              highlightOnHover
              persistTableHead
            />
            <GeneralFooter
              pagination={pagination}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
              dataLength={ucs.length}
            />
          </div>
        </div>
        <CustomConfirmModal
          isVisible={isModalVisible}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          message={`Are you sure you want to delete the uc "${selectedUcForDelete?.name}"?`}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default Union;
